import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MaterialDialog } from "@/components/materials/MaterialDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Materials() {
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<{
    id: string;
    name: string;
    sku: string | null;
    category: string | null;
    unit: string;
    unit_cost: number | null;
    description: string | null;
    supplier: string | null;
    is_active: boolean | null;
  } | null>(null);
  const [deletingMaterialId, setDeletingMaterialId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: materials, isLoading } = useQuery({
    queryKey: ["materials", search],
    queryFn: async () => {
      let query = supabase
        .from("materials")
        .select("*")
        .is("deleted_at", null)
        .order("name");

      if (search) {
        query = query.or(`name.ilike.%${search}%,sku.ilike.%${search}%,category.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("materials")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      toast.success("Materiaali poistettu");
      setDeletingMaterialId(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });


  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case "Kivet":
        return "bg-stone-500";
      case "Pohjat":
        return "bg-slate-500";
      case "Kirjoitukset":
        return "bg-amber-500";
      case "Työaika":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-8 w-8" />
          <h1 className="text-3xl font-bold">Materiaalit</h1>
        </div>
        <Button onClick={() => { setEditingMaterial(null); setIsDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Lisää materiaali
        </Button>
      </div>

      <div className="flex gap-4">
        <Input
          placeholder="Hae materiaaleista..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {isLoading ? (
        <div>Ladataan...</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>Nimi</TableHead>
              <TableHead>Kategoria</TableHead>
              <TableHead>Yksikkö</TableHead>
              <TableHead>Yksikköhinta</TableHead>
              <TableHead>Tila</TableHead>
              <TableHead className="text-right">Toiminnot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {materials?.map((material) => (
              <TableRow key={material.id}>
                <TableCell className="font-mono text-sm">{material.sku || "-"}</TableCell>
                <TableCell className="font-medium">{material.name}</TableCell>
                <TableCell>
                  {material.category && (
                    <Badge className={getCategoryColor(material.category)}>
                      {material.category}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{material.unit}</TableCell>
                <TableCell>{(material.unit_cost ?? 0).toFixed(2)} €</TableCell>
                <TableCell>
                  <Badge variant={material.is_active ? "default" : "secondary"}>
                    {material.is_active ? "Aktiivinen" : "Ei aktiivinen"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingMaterial(material);
                        setIsDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingMaterialId(material.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <MaterialDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        material={editingMaterial}
      />

      <AlertDialog open={!!deletingMaterialId} onOpenChange={(open) => !open && setDeletingMaterialId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vahvista poisto</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko varmasti poistaa tämän materiaalin? Tämä toiminto merkitsee materiaalin poistetuksi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction onClick={() => deletingMaterialId && deleteMutation.mutate(deletingMaterialId)}>
              Poista
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </Layout>
  );
}
